import { useCallback, useMemo } from 'react'
import { useToast } from '@chakra-ui/react'
import apiService from '@/services/api'
import { useApiResource } from './useApiResource'

export interface Plan {
  id: number
  name: string
  slug: string
  description: string
  type: 'preset' | 'custom'
  is_active: boolean
  limits: {
    file_limit: number
    page_limit: number
    smart_tagging_limit: number
    smart_renaming_limit: number
    chatbot_queries_limit: number
    storage_limit_gb: number
    users_limit: number
  }
  price_monthly: number | null
  price_yearly: number | null
  features: string[]
  organizations_count: number
  created_at: string
}

interface UsePlansReturn {
  plans: Plan[]
  loading: boolean
  error: string | null
  loadPlans: () => Promise<void>
  deletePlan: (plan: Plan) => Promise<void>
  refreshPlans: () => Promise<void>
  stats: {
    total: number
    active: number
    custom: number
    totalOrganizations: number
  }
}

export function usePlans(): UsePlansReturn {
  const { data: plans, loading, error, refresh, setData: setPlans } = useApiResource<Plan[]>(
    () => apiService.getAllPlans(),
    [],
    { errorToastTitle: 'Error loading plans' }
  )
  const toast = useToast()

  const deletePlan = useCallback(
    async (plan: Plan) => {
      if (plan.organizations_count > 0) {
        toast({
          title: 'Cannot delete plan',
          description: `This plan is assigned to ${plan.organizations_count} organization(s)`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        return
      }

      const confirmed = window.confirm(`Are you sure you want to delete the plan "${plan.name}"?`)
      if (!confirmed) return

      try {
        await apiService.deletePlan(plan.id)
        setPlans((prev) => prev.filter((p) => p.id !== plan.id))
        toast({
          title: 'Plan deleted',
          description: `${plan.name} has been deleted successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to delete plan'
        toast({
          title: 'Error deleting plan',
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
        await refresh()
      }
    },
    [toast, setPlans, refresh]
  )

  const stats = useMemo(
    () => ({
      total: plans.length,
      active: plans.filter((p) => p.is_active).length,
      custom: plans.filter((p) => p.type === 'custom').length,
      totalOrganizations: plans.reduce((sum, p) => sum + p.organizations_count, 0),
    }),
    [plans]
  )

  return {
    plans,
    loading,
    error,
    loadPlans: refresh,
    deletePlan,
    refreshPlans: refresh,
    stats,
  }
}

export default usePlans
